import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../services/api';
import toast from 'react-hot-toast';
import Modal from '../../components/common/Modal';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

const ClassManagement = () => {
  const [classes, setClasses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showClassModal, setShowClassModal] = useState(false);
  const [showSubjectModal, setShowSubjectModal] = useState(false);
  const [showFormMasterModal, setShowFormMasterModal] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);

  const classSchema = Yup.object().shape({
    class_name: Yup.string().required('Class name is required'),
  });

  const subjectAssignmentSchema = Yup.object().shape({
    subject_id: Yup.number().required('Subject is required'),
  });

  const formMasterSchema = Yup.object().shape({
    staff_email: Yup.string().email('Invalid email').required('Staff email is required'),
  });

  useEffect(() => {
    fetchData();
  }, []);



// Update handleCreateClass function for editing
const handleCreateClass = async (values, { setSubmitting, resetForm }) => {
  try {
    if (editingClass) {
      await adminAPI.updateClass(editingClass.id, values);
      toast.success('Class updated successfully');
    } else {
      await adminAPI.createClass(values);
      toast.success('Class created successfully');
    }
    
    setShowClassModal(false);
    resetForm();
    setEditingClass(null);
    fetchData();
  } catch (error) {
    toast.error(error.response?.data?.detail || 'Operation failed');
  } finally {
    setSubmitting(false);
  }
};



 

 // Update handleDeleteClass function
const handleDeleteClass = async (id) => {
  if (window.confirm('Are you sure you want to delete this class? This action cannot be undone.')) {
    try {
      await adminAPI.deleteClass(id);
      toast.success('Class deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete class');
    }
  }
};




  // Update the handleAssignSubject function
const handleAssignSubject = async (values, { setSubmitting, resetForm }) => {
  try {
    // Ensure all required fields are present
    if (!selectedClass || !selectedClass.id) {
      toast.error('No class selected');
      return;
    }

    const assignmentData = {
      class_id: selectedClass.id,
      subject_id: values.subject_id,
      staff_email: values.staff_email || null, // Make optional
    };

    await adminAPI.assignSubject(assignmentData);
    toast.success('Subject assigned successfully');
    setShowSubjectModal(false);
    resetForm();
    fetchData(); // Refresh data
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
    toast.error(errorMsg);
    console.error('Assign subject error:', error);
  } finally {
    setSubmitting(false);
  }
};

const handleAssignFormMaster = async (values, { setSubmitting, resetForm }) => {
  try {
    if (!selectedClass || !selectedClass.id) {
      toast.error('No class selected');
      return;
    }

    const assignmentData = {
      staff_email: values.staff_email,
      class_id: selectedClass.id,
    };

    await adminAPI.assignFormMaster(assignmentData);
    toast.success('Form master assigned successfully');
    setShowFormMasterModal(false);
    resetForm();
    fetchData(); // Refresh data
  } catch (error) {
    const errorMsg = error.response?.data?.detail || error.message || 'Operation failed';
    toast.error(errorMsg);
    console.error('Assign form master error:', error);
  } finally {
    setSubmitting(false);
  }
};



// Add function to remove subject assignment from class
const handleRemoveSubjectAssignment = async (classId, assignmentId) => {
  if (window.confirm('Are you sure you want to remove this subject assignment?')) {
    try {
      await adminAPI.removeAssignment(assignmentId);
      toast.success('Subject assignment removed successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove assignment');
    }
  }
};

// Add function to remove form master
const handleRemoveFormMaster = async (classId) => {
  if (window.confirm('Are you sure you want to remove the form master?')) {
    try {
      await adminAPI.removeFormMaster(classId); // You need to create this endpoint
      toast.success('Form master removed successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove form master');
    }
  }
};




// Also update the useEffect to handle initial data loading better
useEffect(() => {
  fetchData();
}, []);



const fetchData = async () => {
  try {
    setLoading(true);
    const [classesRes, subjectsRes, staffRes] = await Promise.all([
      adminAPI.getClassesDetailed(), // Changed to detailed endpoint
      adminAPI.getSubjects(),
      adminAPI.getStaff(),
    ]);
    
    setClasses(classesRes.data);
    setSubjects(subjectsRes.data);
    setStaff(staffRes.data.filter(s => s.staff_role === 'lead_two'));
  } catch (error) {
    console.error('Fetch data error:', error);
    toast.error('Failed to load data');
    setClasses([]);
    setSubjects([]);
    setStaff([]);
  } finally {
    setLoading(false);
  }
};

// Also update the API service to properly handle requests
// In your api.js file, ensure the assignSubject and assignFormMaster methods are correct:
// They should send JSON data, not query parameters

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Class Management</h1>
          <p className="text-gray-600">Manage classes, assign subjects and form masters</p>
        </div>
        <button
          onClick={() => {
            setEditingClass(null);
            setShowClassModal(true);
          }}
          className="btn-primary flex items-center"
        >
          <i className="fas fa-plus mr-2"></i> Add Class
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Classes</p>
              <p className="text-2xl font-bold">{classes.length}</p>
            </div>
            <div className="p-3 bg-primary-100 rounded-lg">
              <i className="fas fa-school text-primary-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Available Subjects</p>
              <p className="text-2xl font-bold">{subjects.length}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <i className="fas fa-book text-green-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Form Masters</p>
              <p className="text-2xl font-bold">{staff.length}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-lg">
              <i className="fas fa-chalkboard-teacher text-yellow-600 text-xl"></i>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Assigned Subjects</p>
              <p className="text-2xl font-bold">
                {classes.reduce((acc, cls) => acc + (cls.assigned_subjects?.length || 0), 0)}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <i className="fas fa-tasks text-purple-600 text-xl"></i>
            </div>
          </div>
        </div>
      </div>

      {/* Classes Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
{classes.map((cls) => (
  <div key={cls.id} className="bg-white rounded-lg shadow-lg overflow-hidden">
    <div className="p-6">
      <div className="flex justify-between items-start mb-4">
        <div>
          <h3 className="text-xl font-bold text-gray-800">{cls.class_name}</h3>
          <p className="text-gray-600 text-sm">Class ID: {cls.id}</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              setEditingClass(cls);
              setShowClassModal(true);
            }}
            className="text-primary-600 hover:text-primary-800"
          >
            <i className="fas fa-edit"></i>
          </button>
          <button
            onClick={() => handleDeleteClass(cls.id)}
            className="text-red-600 hover:text-red-800"
          >
            <i className="fas fa-trash"></i>
          </button>
        </div>
      </div>

      {/* Enhanced Class Details */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-gray-600">Form Master:</span>
          <div className="flex items-center">
            <span className="font-medium">
              {cls.form_master?.staff_name || 'Not assigned'}
            </span>
            {cls.form_master && (
              <button
                onClick={() => handleRemoveFormMaster(cls.id)}
                className="ml-2 text-red-500 hover:text-red-700"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-600">Student Count:</span>
          <span className="font-medium">
            {cls.student_count || 0} students
          </span>
        </div>

        <div className="flex items-center justify-between">
          <span className="text-gray-600">Assigned Subjects:</span>
          <span className="font-medium">
            {cls.total_subjects || 0} subjects
          </span>
        </div>

        {/* Assigned Subjects List */}
        {cls.assigned_subjects?.length > 0 && (
          <div className="mt-4">
            <h4 className="font-medium text-gray-700 mb-2">Subjects:</h4>
            <div className="space-y-2">
              {cls.assigned_subjects.map((subject) => (
                <div key={subject.assignment_id} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                  <div>
                    <span className="font-medium">{subject.subject_name}</span>
                    <span className="text-gray-500 ml-2">({subject.subject_code})</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-gray-600 mr-2">
                      {subject.teacher_name || 'No teacher'}
                    </span>
                    <button
                      onClick={() => handleRemoveSubjectAssignment(cls.id, subject.assignment_id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-gray-600">Created:</span>
          <span className="text-sm text-gray-500">
            {new Date(cls.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="mt-6 space-y-2">
        <button
          onClick={() => {
            setSelectedClass(cls);
            setShowFormMasterModal(true);
          }}
          className="w-full btn-secondary py-2 flex items-center justify-center"
        >
          <i className="fas fa-user-tie mr-2"></i>
          {cls.form_master ? 'Change Form Master' : 'Assign Form Master'}
        </button>
        <button
          onClick={() => {
            setSelectedClass(cls);
            setShowSubjectModal(true);
          }}
          className="w-full btn-primary py-2 flex items-center justify-center"
        >
          <i className="fas fa-book mr-2"></i>
          Assign Subjects
        </button>
      </div>
    </div>
  </div>
))}
      </div>

      {/* Empty State */}
      {classes.length === 0 && (
        <div className="text-center py-12">
          <i className="fas fa-school text-4xl text-gray-300 mb-4"></i>
          <p className="text-gray-500">No classes created yet</p>
          <p className="text-sm text-gray-400 mt-1">Create your first class to get started</p>
          <button
            onClick={() => setShowClassModal(true)}
            className="mt-4 btn-primary"
          >
            <i className="fas fa-plus mr-2"></i> Create First Class
          </button>
        </div>
      )}

      {/* Create/Edit Class Modal */}
      <Modal
        isOpen={showClassModal}
        onClose={() => {
          setShowClassModal(false);
          setEditingClass(null);
        }}
        title={editingClass ? 'Edit Class' : 'Create New Class'}
      >
        <Formik
          initialValues={{
            class_name: editingClass?.class_name || '',
          }}
          validationSchema={classSchema}
          onSubmit={handleCreateClass}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Class Name *
                </label>
                <Field
                  type="text"
                  name="class_name"
                  className="input-field"
                  placeholder="e.g., Grade 10A, Primary 5B"
                />
                {errors.class_name && touched.class_name && (
                  <div className="text-red-500 text-sm mt-1">{errors.class_name}</div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowClassModal(false);
                    setEditingClass(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save mr-2"></i>
                      {editingClass ? 'Update' : 'Create'}
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Assign Subject Modal */}
      <Modal
        isOpen={showSubjectModal}
        onClose={() => {
          setShowSubjectModal(false);
          setSelectedClass(null);
        }}
        title={`Assign Subjects to ${selectedClass?.class_name || 'Class'}`}
      >
        <Formik
          initialValues={{
            subject_id: '',
            staff_email: '',
          }}
          validationSchema={subjectAssignmentSchema}
          onSubmit={handleAssignSubject}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Subject *
                </label>
                <Field as="select" name="subject_id" className="input-field">
                  <option value="">Choose a subject</option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject_name}
                    </option>
                  ))}
                </Field>
                {errors.subject_id && touched.subject_id && (
                  <div className="text-red-500 text-sm mt-1">{errors.subject_id}</div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Assign to Teacher
                </label>
                <Field as="select" name="staff_email" className="input-field">
                  <option value="">Select teacher (optional)</option>
                  {staff.map((teacher) => (
                    <option key={teacher.staff_email} value={teacher.staff_email}>
                      {teacher.staff_name} ({teacher.staff_email})
                    </option>
                  ))}
                </Field>
              </div>

              {selectedClass && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-medium text-gray-700 mb-2">Current Assigned Subjects</h4>
                  {selectedClass.assigned_subjects?.length > 0 ? (
                    <ul className="space-y-2">
                      {selectedClass.assigned_subjects.map((subject, index) => (
                        <li key={index} className="flex justify-between items-center text-sm">
                          <span>{subject.subject_name}</span>
                          <span className="text-gray-500">{subject.teacher_name || 'No teacher'}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-500 text-sm">No subjects assigned yet</p>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowSubjectModal(false);
                    setSelectedClass(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Assigning...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-plus mr-2"></i>
                      Assign Subject
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>

      {/* Assign Form Master Modal */}
      <Modal
        isOpen={showFormMasterModal}
        onClose={() => {
          setShowFormMasterModal(false);
          setSelectedClass(null);
        }}
        title={`Assign Form Master to ${selectedClass?.class_name || 'Class'}`}
      >
        <Formik
          initialValues={{
            staff_email: selectedClass?.form_master_email || '',
          }}
          validationSchema={formMasterSchema}
          onSubmit={handleAssignFormMaster}
        >
          {({ isSubmitting, errors, touched }) => (
            <Form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select Form Master *
                </label>
                <Field as="select" name="staff_email" className="input-field">
                  <option value="">Choose a form master</option>
                  {staff.map((teacher) => (
                    <option key={teacher.staff_email} value={teacher.staff_email}>
                      {teacher.staff_name} ({teacher.staff_email})
                    </option>
                  ))}
                </Field>
                {errors.staff_email && touched.staff_email && (
                  <div className="text-red-500 text-sm mt-1">{errors.staff_email}</div>
                )}
              </div>

              {selectedClass && selectedClass.form_master && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <i className="fas fa-exclamation-triangle text-yellow-500 mr-2"></i>
                    <p className="text-yellow-700 text-sm">
                      Currently assigned to: <strong>{selectedClass.form_master}</strong>
                    </p>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowFormMasterModal(false);
                    setSelectedClass(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary px-4 py-2"
                >
                  {isSubmitting ? (
                    <>
                      <i className="fas fa-spinner fa-spin mr-2"></i>
                      Assigning...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-user-tie mr-2"></i>
                      Assign Form Master
                    </>
                  )}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  );
};

export default ClassManagement;